import React, { useState } from "react";
import { Avatar, Box, Button, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import color from "../../../Constants/colors";

interface Props {
  name: string;
  text: string;
  hashtags: string[];
  firstComment: string | null;
  imageUrl?: string | null;
}

export const LINKEDIN_MAX_CHARS = 3000;
const LINKEDIN_CUT_OFF = 210;

export const fullPostText = (text: string, hashtags: string[]) => {
  const tags = hashtags.map((tag) => `#${tag.replace(/^#/, "").replace(/\s+/g, "")}`);
  return [text.trim(), tags.join(" ")].filter((part) => part).join("\n\n");
};

const VoicesLinkedInPreview: React.FC<Props> = ({
  name,
  text,
  hashtags,
  firstComment,
  imageUrl,
}) => {
  const { t } = useTranslation("voicesPosts");
  const [expanded, setExpanded] = useState(false);

  const full = fullPostText(text, hashtags);
  const cut = !expanded && full.length > LINKEDIN_CUT_OFF;
  const shown = cut ? `${full.slice(0, LINKEDIN_CUT_OFF).trimEnd()}…` : full;

  return (
    <Box>
      <Box
        sx={{
          border: `1px solid ${color.gray200}`,
          borderRadius: 2,
          backgroundColor: "white",
          p: 2,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 1.5 }}>
          <Avatar sx={{ width: 40, height: 40, bgcolor: color.gray200, color: color.gray700 }}>
            {name.charAt(0)}
          </Avatar>
          <Box>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              {name}
            </Typography>
            <Typography variant="caption">{t("previewNow")}</Typography>
          </Box>
        </Box>
        <Typography variant="body2" sx={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
          {shown || <span style={{ color: color.gray500 }}>{t("previewEmpty")}</span>}
        </Typography>
        {full.length > LINKEDIN_CUT_OFF && (
          <Button
            size="small"
            onClick={() => setExpanded(!expanded)}
            sx={{ color: color.gray600, textTransform: "none", px: 0, minWidth: 0 }}
          >
            {expanded ? t("previewLess") : t("previewMore")}
          </Button>
        )}
        {imageUrl && (
          <Box
            component="img"
            src={imageUrl}
            alt=""
            sx={{ display: "block", width: "100%", mt: 1.5, borderRadius: 1 }}
          />
        )}
      </Box>
      {firstComment && (
        <Box sx={{ mt: 1, ml: 4, p: 1.5, borderRadius: 2, backgroundColor: color.gray100 }}>
          <Typography variant="caption" sx={{ fontWeight: 600, display: "block" }}>
            {t("previewFirstComment")}
          </Typography>
          <Typography variant="body2" sx={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
            {firstComment}
          </Typography>
        </Box>
      )}
      <Typography
        variant="caption"
        sx={{
          display: "block",
          mt: 1,
          textAlign: "right",
          color: full.length > LINKEDIN_MAX_CHARS ? color.red700 : color.gray600,
        }}
      >
        {t("characters", { count: full.length, max: LINKEDIN_MAX_CHARS })}
      </Typography>
    </Box>
  );
};

export default VoicesLinkedInPreview;
